/**
 * `/settings/courses` — course selection (#436, UX_EXPERIENCE §4.3), under Settings since #581. It was `/setup`, which now
 * redirects here.
 *
 * One row per course in the pilot's catalog: a checkbox, a thin colour bar beside the course code and title, and the next
 * due line. The colour is an identifier only: it is never the sole cue (the code and title always sit next to it) and it
 * never marks a status or a verdict. Each tick or colour change is sent at once and applied optimistically; a failure
 * reverts it and says so.
 *
 * Course titles are Moodle text, untrusted: rendered as plain text through React, with `dir="auto"` because they can be
 * Hebrew. No count, score or percentage appears here.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { COURSE_PALETTE, COURSE_SYMBOLS } from '@davinci/contracts';
import type { CourseSymbolKey } from '@davinci/contracts';
import type { CourseResponse, CoursesResponse, CourseUpdateRequest } from '@davinci/contracts';
import { courseShortName, courseTitle } from '../../app/course-names.js';
import { useApi } from '../../app/api-context.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { queryKeys } from '../../api/query-keys.js';
import { useLocale, useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';
import type { UiLocale } from '../../i18n/locale.js';
import { CourseSymbol, SYMBOL_LABELS } from '../../app/course-symbol.js';
import { AssessmentsSection } from '../setup/assessments-section.js';

export const courseMessages = defineMessages({
  en: {
    title: 'Courses and colours',
    intro: 'Which courses show and how they look. This is a display preference only: it changes nothing that is recorded.',
    loading: 'Loading your courses…',
    empty: 'No courses yet. They appear after the next Moodle sync.',
    noDue: 'No upcoming due dates',
    nextDue: 'Next due:',
    colourFor: (code: string) => `Colour for ${code}`,
    shortName: 'Short name',
    symbol: 'Symbol',
    automatic: 'Automatic',
    resetDefault: 'Reset to default',
    shown: 'Shown',
    shortNote: 'Default: the title without "(1)" or "for … students". Shorten it if you like.',
    symbolNote: 'Chosen from the course number in the catalogue, never from the title.',
    automaticWith: (label: string) => `Automatic: ${label}`,
    slate: 'slate',
    teal: 'teal',
    plum: 'plum',
    ochre: 'ochre',
    moss: 'moss',
    rose: 'rose',
  },
  he: {
    title: 'קורסים וצבעים',
    intro: 'אילו קורסים מוצגים ואיך הם נראים. זאת העדפת תצוגה בלבד: היא לא משנה שום דבר שנרשם.',
    loading: 'טוען את הקורסים שלך…',
    empty: 'עוד אין קורסים. הם יופיעו אחרי הסנכרון הבא עם Moodle.',
    noDue: 'אין מועדי הגשה קרובים',
    nextDue: 'ההגשה הבאה:',
    colourFor: (code: string) => `צבע עבור ${code}`,
    shortName: 'שם קצר',
    symbol: 'סמל',
    automatic: 'אוטומטי',
    resetDefault: 'חזרה לברירת המחדל',
    shown: 'מוצג',
    shortNote: 'ברירת המחדל: השם בלי "(1)" ובלי "לתלמידי…". אפשר לקצר.',
    symbolNote: 'נבחר לפי מספר הקורס בקטלוג, לא לפי השם.',
    automaticWith: (label: string) => `אוטומטי: ${label}`,
    slate: 'אפור צפחה',
    teal: 'טורקיז',
    plum: 'שזיף',
    ochre: 'אוכרה',
    moss: 'ירוק טחב',
    rose: 'ורוד',
  },
});

interface CourseChange {
  readonly courseRef: string;
  readonly body: CourseUpdateRequest;
  /** Minted per change and kept for a retry, so a retry replays rather than repeats. */
  readonly idempotencyKey: string;
}

export function formatDue(dueAt: string, locale: UiLocale): string {
  const at = new Date(dueAt);
  if (Number.isNaN(at.getTime())) return dueAt;
  return new Intl.DateTimeFormat(locale === 'he' ? 'he' : undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(at);
}

/** How long, and how often, the welcome flow asks again while the first Moodle sync has not brought any course. */
const COURSES_POLL_MS = 3000;
const COURSES_POLL_WINDOW_MS = 60_000;

/**
 * The course list and the one PATCH behind every tick and colour, shared with the welcome flow (#648). With
 * `pollWhileEmpty` the list is fetched again every few seconds while it is empty, for at most about a minute, because
 * the first sync after connecting Moodle can finish after the first fetch; `waiting` says that is still going on.
 */
export function useCourseUpdates(options: { readonly pollWhileEmpty?: boolean } = {}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [failed, setFailed] = useState<{ error: unknown; change: CourseChange } | undefined>(undefined);
  const pollWhileEmpty = options.pollWhileEmpty === true;
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    if (!pollWhileEmpty) return;
    const timer = setTimeout(() => setGaveUp(true), COURSES_POLL_WINDOW_MS);
    return () => clearTimeout(timer);
  }, [pollWhileEmpty]);

  const courses = useQuery({
    queryKey: queryKeys.courses,
    queryFn: () => api.getCourses(),
    refetchInterval: (query) =>
      pollWhileEmpty && !gaveUp && query.state.data !== undefined && query.state.data.courses.length === 0 ? COURSES_POLL_MS : false,
  });
  const waiting = pollWhileEmpty && !gaveUp && courses.isSuccess && courses.data.courses.length === 0;

  const mutation = useMutation({
    mutationFn: (change: CourseChange) => api.updateCourse(change.courseRef, change.idempotencyKey, change.body),
    onMutate: async (change) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.courses });
      const previous = queryClient.getQueryData<CoursesResponse>(queryKeys.courses);
      if (previous !== undefined) {
        queryClient.setQueryData<CoursesResponse>(queryKeys.courses, {
          courses: previous.courses.map((course) =>
            // Only the tick and colour are optimistic; a short name or symbol (#574) waits for the server, which derives the defaults.
            course.moodleCourseId === change.courseRef
              ? { ...course, ...(change.body.selected === undefined ? {} : { selected: change.body.selected }), ...(change.body.colour === undefined ? {} : { colour: change.body.colour }) }
              : course,
          ),
        });
      }
      setFailed(undefined);
      return { previous };
    },
    onError: (error, change, context) => {
      if (context?.previous !== undefined) queryClient.setQueryData(queryKeys.courses, context.previous);
      setFailed({ error, change });
    },
    onSuccess: (saved) => {
      queryClient.setQueryData<CoursesResponse>(queryKeys.courses, (current) =>
        current === undefined
          ? current
          : { courses: current.courses.map((c) => (c.moodleCourseId === saved.moodleCourseId ? saved : c)) },
      );
    },
  });

  function change(course: CourseResponse, body: CourseUpdateRequest): void {
    mutation.mutate({ courseRef: course.moodleCourseId, body, idempotencyKey: crypto.randomUUID() });
  }

  return { courses, mutation, failed, change, waiting };
}

export function CoursesPage(): React.JSX.Element {
  const t = useMessages(courseMessages);
  const { courses, mutation, failed, change } = useCourseUpdates();

  return (
    <div className="page">
      <h2>{t.title}</h2>
      <p>{t.intro}</p>

      {courses.isPending ? <p aria-live="polite">{t.loading}</p> : null}

      {courses.isError ? <FailureNotice error={courses.error} onRetry={() => void courses.refetch()} /> : null}

      {failed !== undefined ? (
        <FailureNotice error={failed.error} onRetry={() => mutation.mutate(failed.change)} />
      ) : null}

      {courses.isSuccess && courses.data.courses.length === 0 ? (
        <p>{t.empty}</p>
      ) : null}

      {courses.isSuccess && courses.data.courses.length > 0 ? (
        <ul className="settings-courses">
          {courses.data.courses.map((course) => (
            <CourseRow key={course.moodleCourseId} course={course} onChange={(body) => change(course, body)} />
          ))}
        </ul>
      ) : null}

      {/* #577: Exam prep's "add an assessment" link lands here. */}
      <div id="assessments">
        {courses.isSuccess ? <AssessmentsSection courses={courses.data.courses.filter((c) => c.selected)} /> : null}
      </div>
    </div>
  );
}

function CourseRow({
  course,
  onChange,
}: {
  readonly course: CourseResponse;
  readonly onChange: (body: CourseUpdateRequest) => void;
}): React.JSX.Element {
  const t = useMessages(courseMessages);
  const { locale } = useLocale();
  const id = `course-${course.moodleCourseId}`;
  const shown = courseShortName(course, locale);
  function commitShortName(input: HTMLInputElement): void {
    const value = input.value.trim();
    if (value === '') {
      // Empty means "the default". Already the default: put its text back and send nothing.
      if (course.shortNameIsDefault) input.value = shown;
      else onChange({ shortName: null });
      return;
    }
    if (value !== shown) onChange({ shortName: value });
  }
  const symbolLabel = SYMBOL_LABELS[course.symbol.key][locale];
  return (
    <li className="settings-course" data-course={course.colour} data-course-colour={course.colour}>
      <div className="settings-course-head">
        <span className="settings-course-tile">
          <CourseSymbol symbol={course.symbol} />
        </span>
        <div className="settings-course-title">
          <strong id={`${id}-name`} dir="auto">
            {shown}
          </strong>
          <span>
            <bdi dir="ltr">{course.code}</bdi> &middot; <bdi dir="auto">{courseTitle(course, locale)}</bdi>
          </span>
          <span>
            {course.nextDue === null ? (
              t.noDue
            ) : (
              <>
                {t.nextDue} <bdi dir="auto">{course.nextDue.exerciseTitle}</bdi> &middot;{' '}
                {formatDue(course.nextDue.dueAt, locale)}
              </>
            )}
          </span>
        </div>
        <label className="settings-shown">
          <input
            id={`${id}-selected`}
            type="checkbox"
            checked={course.selected}
            aria-label={`${t.shown}: ${course.code} ${courseTitle(course, locale)}`}
            onChange={(event) => onChange({ selected: event.target.checked })}
          />
          <span aria-hidden="true">{t.shown}</span>
        </label>
      </div>
      <div className="settings-course-fields">
        <div className="settings-course-field">
          <label htmlFor={`${id}-short`}>{t.shortName}</label>
          <input
            id={`${id}-short`}
            key={shown}
            type="text"
            dir="auto"
            maxLength={40}
            defaultValue={shown}
            aria-describedby={`${id}-short-note`}
            onBlur={(event) => commitShortName(event.target)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.currentTarget.blur();
            }}
          />
          <span id={`${id}-short-note`} className="settings-note">
            {t.shortNote}
          </span>
          {course.shortNameIsDefault ? null : (
            <button type="button" className="button-neutral settings-small" onClick={() => onChange({ shortName: null })}>
              {t.resetDefault}
            </button>
          )}
        </div>
        <div className="settings-course-field">
          <label htmlFor={`${id}-symbol`}>{t.symbol}</label>
          <select
            id={`${id}-symbol`}
            value={course.symbolIsDefault ? '' : course.symbol.key}
            aria-describedby={`${id}-symbol-note`}
            onChange={(event) =>
              onChange({ symbol: event.target.value === '' ? null : (event.target.value as CourseSymbolKey) })
            }
          >
            <option value="">{course.symbolIsDefault ? t.automaticWith(symbolLabel) : t.automatic}</option>
            {COURSE_SYMBOLS.map((key) => (
              <option key={key} value={key}>
                {SYMBOL_LABELS[key][locale]}
              </option>
            ))}
          </select>
          <span id={`${id}-symbol-note`} className="settings-note">
            {t.symbolNote}
          </span>
        </div>
      </div>
      <fieldset className="settings-swatches">
        <legend>{t.colourFor(course.code)}</legend>
        <div className="settings-swatch-row">
          {COURSE_PALETTE.map((colour) => (
            <label key={colour} className="settings-swatch" data-course={colour} data-checked={course.colour === colour}>
              <input
                type="radio"
                name={`${id}-colour`}
                value={colour}
                checked={course.colour === colour}
                onChange={() => onChange({ colour })}
              />
              <span className="settings-swatch-dot" aria-hidden="true" />
              <span>{t[colour]}</span>
            </label>
          ))}
        </div>
      </fieldset>
    </li>
  );
}
